import { Control, FieldPath, FieldValues } from "react-hook-form";
import { cn } from "../../../../lib/utils";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/common/form";
import { Input } from "../../../ui/common/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../ui/common/select";

interface UnitOption {
  key?: string | number;
  value: string | number;
  label?: string;
  text?: string;
}

interface ValidationRules {
  min?: {
    value: number;
    message: string;
  };
  max?: {
    value: number;
    message: string;
  };
  validate?: Record<string, (value: any) => boolean | string> | ((value: any) => boolean | string);
}

interface FormUnitInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TUnitName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string | null;
  required?: boolean | string;
  placeholder?: string;
  errorMessage?: string;
  // Validation rules
  min?: number | { value: number; message: string };
  max?: number | { value: number; message: string };
  step?: number | string;
  validate?: Record<string, (value: any) => boolean | string> | ((value: any) => boolean | string);
  // Optional unit support
  unitType?: string;
  unitOptions?: UnitOption[];
  unitName?: TUnitName;
  unitRequired?: boolean;
  unitErrorMessage?: string;
  // FormInputNumber features
  enableMinus?: boolean;
  removeDecimal?: boolean;
  enableDecimalWithMinus?: boolean;
  enableLimitedDecimal?: boolean;
  viewDecimalNumber?: number;
  formatWithCommas?: boolean;
  disabled?: boolean;
}

export function FormUnitInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TUnitName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  placeholder = "0",
  errorMessage,
  min,
  max,
  step = "any",
  validate,
  unitType,
  unitOptions,
  unitName,
  unitRequired = false,
  unitErrorMessage,
  enableMinus = false,
  removeDecimal = false,
  enableDecimalWithMinus = false,
  enableLimitedDecimal = false,
  viewDecimalNumber = 4,
  formatWithCommas: shouldFormatWithCommas = false,
  disabled = false
}: FormUnitInputProps<TFieldValues, TName, TUnitName>) {
  // Format number with commas
  const formatWithCommas = (value: string) => {
    if (value === null || value === undefined) return "";
    const strValue = String(value);
    const isNegative = strValue.startsWith("-");
    const cleanValue = strValue.replace(/[^0-9.]/g, "");
    const [integerPart, decimalPart] = cleanValue.split(".");
    const int = parseInt(integerPart);
    const formattedInt = isNaN(int) ? "" : integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const formattedValue =
      decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;

    return isNegative ? `-${formattedValue}` : formattedValue;
  };

  // Process input value
  const processInput = (value: any): string => {
    value = String(value ?? "");

    // If enableDecimalWithMinus is true, it implies enableMinus should be true
    const allowMinus = enableMinus || enableDecimalWithMinus;

    if (allowMinus) {
      value = enableDecimalWithMinus
        ? value.replace(/[^0-9.-]/g, "")
        : value.replace(/[^0-9-]/g, "");

      if (value.includes("-")) {
        const minusCount = (value.match(/-/g) || []).length;
        if (minusCount > 1) {
          value = value.replace(/-/g, (match: any, offset: any) => (offset === 0 ? "-" : ""));
        }
        if (value.indexOf("-") > 0) {
          value = value.replace("-", "");
          value = "-" + value;
        }
      }

      if (enableDecimalWithMinus) {
        const dotCount = (value.match(/\./g) || []).length;
        if (dotCount > 1) {
          const firstDotIndex = value.indexOf(".");
          value = value.replace(/\./g, (match: any, offset: any) =>
            offset === firstDotIndex ? "." : ""
          );
        }
      }
      return value;
    }

    value = removeDecimal ? value.replace(/[^0-9]/g, "") : value.replace(/[^0-9.]/g, "");

    if (!removeDecimal) {
      const dotCount = (value.match(/\./g) || []).length;
      if (dotCount > 1) {
        const firstDotIndex = value.indexOf(".");
        value = value.replace(/\./g, (match: any, offset: any) =>
          offset === firstDotIndex ? "." : ""
        );
      }
    }

    if (enableLimitedDecimal && !removeDecimal) {
      const [intPart, decimalPart] = value.split(".");
      if (decimalPart && decimalPart.length > viewDecimalNumber) {
        value = `${intPart}.${decimalPart.substring(0, viewDecimalNumber)}`;
      }
    }

    return value;
  };

  // Process output value (clean value for form state)
  const processOutput = (value: string): any => {
    if (!value || value === "") return "";

    // For minus mode (including enableDecimalWithMinus), return as-is
    const allowMinus = enableMinus || enableDecimalWithMinus;
    if (allowMinus) {
      // Remove any invalid characters but keep minus and decimal
      const cleaned = enableDecimalWithMinus
        ? value.replace(/[^0-9.-]/g, "")
        : value.replace(/[^0-9-]/g, "");
      return cleaned;
    }

    // For normal mode, clean and return
    const cleanedNumber = value.replace(/[^0-9.]/g, "");

    // If it's just a decimal point, return it so user can continue typing
    if (cleanedNumber === ".") return "0.";

    return cleanedNumber || "";
  };

  // Build validation rules
  const rules: any = {};

  // Helper to check if value is empty
  const isEmpty = (value: any) => {
    const strValue = String(value ?? "").trim();
    return strValue === "" || strValue === "0." || strValue === "-" || strValue === ".";
  };

  if (required) {
    rules.required = {
      value: true,
      message: typeof required === "string" ? required : errorMessage || `${label} is required`
    };

    // Additional validation to ensure the value is not empty or just whitespace
    rules.validate = {
      ...rules.validate,
      notEmpty: (value: any) => {
        if (isEmpty(value)) {
          return errorMessage || `${label} is required`;
        }
        return true;
      }
    };
  }

  // Only apply min/max rules if field is required OR has a value
  if (min !== undefined) {
    if (!required) {
      // For non-required fields, only validate min if there's a value
      rules.validate = {
        ...rules.validate,
        minValue: (value: any) => {
          if (isEmpty(value)) return true; // Skip validation if empty
          const numValue = parseFloat(String(value));
          const minValue = typeof min === "number" ? min : min.value;
          const minMessage =
            typeof min === "number" ? `${label} must be at least ${min}` : min.message;
          return numValue >= minValue || minMessage;
        }
      };
    } else {
      // For required fields, apply standard min rule
      if (typeof min === "number") {
        rules.min = {
          value: min,
          message: `${label} must be at least ${min}`
        };
      } else {
        rules.min = min;
      }
    }
  }

  if (max !== undefined) {
    if (!required) {
      // For non-required fields, only validate max if there's a value
      rules.validate = {
        ...rules.validate,
        maxValue: (value: any) => {
          if (isEmpty(value)) return true; // Skip validation if empty
          const numValue = parseFloat(String(value));
          const maxValue = typeof max === "number" ? max : max.value;
          const maxMessage =
            typeof max === "number" ? `${label} must be at most ${max}` : max.message;
          return numValue <= maxValue || maxMessage;
        }
      };
    } else {
      // For required fields, apply standard max rule
      if (typeof max === "number") {
        rules.max = {
          value: max,
          message: `${label} must be at most ${max}`
        };
      } else {
        rules.max = max;
      }
    }
  }

  if (validate) {
    if (!required) {
      // For non-required fields, wrap custom validations to skip when empty
      const wrappedValidate =
        typeof validate === "function"
          ? { custom: (value: any) => (isEmpty(value) ? true : validate(value)) }
          : Object.entries(validate).reduce((acc, [key, fn]) => {
              acc[key] = (value: any) => (isEmpty(value) ? true : fn(value));
              return acc;
            }, {} as Record<string, (value: any) => boolean | string>);

      rules.validate = {
        ...rules.validate,
        ...wrappedValidate
      };
    } else {
      // For required fields, apply validations as-is
      rules.validate = {
        ...rules.validate,
        ...validate
      };
    }
  }

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let inputValue = e.target.value.replace(/,/g, "");

          // Process the input to allow only valid characters
          const processedInput = processInput(inputValue);

          // Store the clean value
          const outputValue = processOutput(processedInput);
          field.onChange(outputValue);
        };

        const displayValue = (() => {
          const value = field.value ?? "";
          const rawValue = processInput(value);
          return shouldFormatWithCommas ? formatWithCommas(rawValue) : rawValue;
        })();

        return (
          <FormItem>
            <FormLabel>
              {label}
              {required && !isEmpty(label) && <span className="tw-text-destructive tw-ml-0.5">*</span>}
            </FormLabel>
            <div className="tw-relative tw-flex">
              <FormControl>
                <Input
                  type="text"
                  value={displayValue}
                  onChange={handleChange}
                  onBlur={field.onBlur}
                  placeholder={placeholder}
                  disabled={disabled}
                  className={cn(
                    "tw-text-foreground !tw-pl-2",
                    unitOptions || unitType ? "tw-pr-2 tw-rounded-r-none tw-border-r-0" : ""
                  )}
                />
              </FormControl>

              {/* Dynamic unit selector */}
              {unitOptions && unitName && (
                <FormField
                  control={control}
                  name={unitName}
                  rules={{
                    required: unitRequired
                      ? {
                          value: true,
                          message: unitErrorMessage || "Unit is required"
                        }
                      : false
                  }}
                  render={({ field: unitField }) => (
                    <Select
                      value={String(unitField.value)}
                      disabled={disabled}
                      onValueChange={value => {
                        unitField.onChange(value);
                        field.onBlur();
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="!tw-h-10 tw-min-w-20 tw-w-auto tw-rounded-l-none tw-border-l tw-bg-muted tw-text-muted-foreground tw-text-xs tw-font-medium tw-shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {unitOptions.map(option => (
                          <SelectItem key={option.key || option.value} value={String(option.value)}>
                            {option.label || option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}

              {/* Static unit display */}
              {unitType && !unitOptions && (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-24 tw-rounded-r-md tw-border tw-border-l tw-bg-muted tw-px-2 tw-text-xs tw-font-medium tw-text-muted-foreground tw-shrink-0">
                  {unitType}
                </div>
              )}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
